import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../organisation.module.scss';
import { ReactComponent as OrgMap } from 'assets/icons/org_map.svg';
import TabsComponent from 'shared/components/TabsComponent';
import { ReactComponent as DropLocation } from 'assets/icons/fill_location.svg';
import {
  CompanySetUpTabHash,
  CompanySetupEnum,
  OrganisationMapTitles,
} from 'enums/CompanySetupEnums';
import { Col, Row, Select } from 'antd';
import useFilters from 'shared/hooks/useFilters';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as Draft } from 'assets/icons/draft.svg';
import { MetaData } from 'models/MetaData/metaData.model';
import LocationTable from './locationsTable';
import useRedirect from 'shared/utils/useRedirect';
import OrganisationService from 'services/OrganisationService/organisation.service';
import ViewOrganisationMap from 'views/CompanySetup/OrganisationMap/ViewOrganisationMap';
import { pageCountOne } from 'shared/components/COProductCard/constants';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import { useUserRole } from 'shared/hooks/useUserRole';
import { isViewer, isDataContributor } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';


interface LocationListingProps {
  onTabChange: (tab: string) => void;
  yearsMetaData: MetaData[];
  selectedYear: number | null;
  setSelectedYear: (val: number) => void;
  tableLoading?: boolean;
}

const LocationListing: FC<LocationListingProps> = ({
  onTabChange,
  yearsMetaData,
  selectedYear,
  setSelectedYear,
  tableLoading,
}) => {
  const { redirectToEditOrganization } = useRedirect();
  const { getHash, setHash, getParams, setParams } = useFilters();
  const { organizationData } = useParentOrganizationContext();
  const { organisationYearsList, fetchOrganisationYearsMeta, loading } =
    OrganisationService();
  const [_, setSearchParams] = useSearchParams();
  const userRole = useUserRole();
  const isViewerRole = isViewer(userRole);
  const isDataContributorRole = isDataContributor(userRole);
  const canEditMap = !isViewerRole && !isDataContributorRole;
  const isOrganisationMapTab = getHash() === CompanySetUpTabHash.ORG_MAP;
  const [activeTab, setActiveTab] = useState<string>(
    isOrganisationMapTab ? CompanySetupEnum.ORG_MAP : CompanySetupEnum.LOCATIONS
  );

  const [organisationActiveYear, setOrganisationActiveYear] = useState<number>(
    getParams(['year']).year
  );
  useEffect(() => {
    fetchOrganisationYearsMeta();
  }, []);

  const organisationMapYearOptions = useMemo(() => {
    if (organisationYearsList.length && !getParams(['year']).year) {
      setOrganisationActiveYear(Number(organisationYearsList[0].value));
      isOrganisationMapTab &&
        setParams({ year: organisationYearsList[0].value });
    }
    return organisationYearsList.map(year => {
      return { value: Number(year.value), label: String(year.value) };
    }) as MetaData[];
  }, [organisationYearsList]);

  const handleDropdownChange = (val: number) => {
    if (isOrganisationMapTab) {
      setOrganisationActiveYear(val);
      setParams({ year: val });
    } else {
      setSelectedYear(val);
      const currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.set('year', val.toString());
      currentSearchParams.set('page', pageCountOne.toString());
      setSearchParams(currentSearchParams);
    }
  };
  const handleTabChange = (key: string) => {
    const isOrganisationMapTab = key === CompanySetupEnum.ORG_MAP;
    setActiveTab(key);
    onTabChange(key);
    if (isOrganisationMapTab && organisationYearsList.length) {
      setParams(
        { year: organisationYearsList[0].value },
        CompanySetUpTabHash.ORG_MAP
      );
      setOrganisationActiveYear(Number(organisationYearsList[0].value));
    } else {
      setParams({}, CompanySetUpTabHash.LOCATIONS);
    }
  };

  const tabs = [
    {
      key: CompanySetupEnum.LOCATIONS,
      label: CompanySetupEnum.LOCATIONS,
      icon: <DropLocation />,
      children: <LocationTable tableLoading={tableLoading} />,
    },
    {
      key: CompanySetupEnum.ORG_MAP,
      label: CompanySetupEnum.ORG_MAP,
      icon: <OrgMap />,
      children: <ViewOrganisationMap year={organisationActiveYear} />,
    },
  ];

  const handleRedirectToEditMap = useCallback(() => {
    if (!canEditMap) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    organisationYearsList?.length &&
      redirectToEditOrganization(
        (
          organisationActiveYear ?? organisationYearsList?.[0]?.value
        )?.toString()
      );
  }, [organisationYearsList, organisationActiveYear, canEditMap]);
  if (loading) {
    return null;
  }
  return (
    <div className={styles.wrapper}>
      <Row className={styles.headerContainer}>
        <Col className={styles.rightSideHeader} span={24}>
          {isOrganisationMapTab && (
            <div
              className={styles.editOrganisationContainer}
              onClick={handleRedirectToEditMap}
              style={{
                opacity: canEditMap ? 1 : 0.6,
                cursor: canEditMap ? 'pointer' : 'not-allowed',
              }}>
              <Draft className={styles.icon} />
              {OrganisationMapTitles.EDIT_ORGANISATION_MAP}
            </div>
          )}
          {(isOrganisationMapTab ||
            organizationData?.company?.hasLocations) && (
            <div className={styles.yearFieldContainer}>
              <label className={styles.yearLabel}> Year</label>
              <Select
                value={
                  isOrganisationMapTab ? organisationActiveYear : selectedYear
                }
                listHeight={120}
                suffixIcon={<Arrow fontSize={44} />}
                onChange={handleDropdownChange}
                placeholder="Year"
                options={
                  isOrganisationMapTab
                    ? organisationMapYearOptions
                    : yearsMetaData
                }
                loading={loading}
              />
            </div>
          )}
        </Col>
        <Col className={styles.tabContainer} span={24}>
          <TabsComponent
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </Col>
      </Row>
    </div>
  );
};

export default LocationListing;
